"use client";

import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

export function TableColumnResizeHandle({
  active,
  className,
  width,
  minWidth,
  maxWidth,
  onResize,
  onKeyDown,
  ...props
}: ComponentProps<"button"> & {
  active?: boolean;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  onResize?: (width: number) => void;
}) {
  return (
    <button
      type="button"
      {...props}
      role={onResize ? "separator" : undefined}
      aria-orientation={onResize ? "vertical" : undefined}
      aria-valuenow={width}
      aria-valuemin={minWidth}
      aria-valuemax={maxWidth}
      aria-valuetext={
        width === undefined ? undefined : `${Math.round(width)} pixels`
      }
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (event.defaultPrevented || !onResize || width === undefined) return;
        const step = event.shiftKey ? 40 : 10;
        const next =
          event.key === "ArrowLeft"
            ? width - step
            : event.key === "ArrowRight"
              ? width + step
              : event.key === "Home"
                ? minWidth
                : event.key === "End"
                  ? maxWidth
                  : undefined;
        if (next === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        onResize(Math.min(maxWidth ?? Infinity, Math.max(minWidth ?? 0, next)));
      }}
      className={cn(
        "absolute inset-y-0 -right-1 z-20 w-2 cursor-col-resize touch-none [@media(pointer:coarse)]:-right-3 [@media(pointer:coarse)]:w-6",
        "after:absolute after:inset-y-1 after:left-1/2 after:w-px after:-translate-x-1/2 after:bg-transparent",
        "hover:after:bg-border focus-visible:outline-none focus-visible:after:bg-ring",
        active && "after:bg-ring",
        className,
      )}
    />
  );
}
